import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowRight, Utensils, Croissant, Scissors, Wrench, ShoppingBag, Scale, Sparkles, Briefcase, Store, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { BusinessCard } from "../components/BusinessCard";

const ICONS = { utensils: Utensils, croissant: Croissant, scissors: Scissors, wrench: Wrench, "shopping-bag": ShoppingBag, scale: Scale, sparkles: Sparkles, briefcase: Briefcase };

export default function Home() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.stats().then(setStats).catch(() => {});
    api.categories().then(setCats).catch(() => {});
    api.businesses({ featured: true, limit: 6 }).then(setFeatured).catch(() => {});
    api.landingIndex().then((d) => setCities(d.cities)).catch(() => {});
  }, []);

  const go = (e) => {
    e.preventDefault();
    nav(`/explorar?search=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden ml-grain">
        <div className="absolute inset-0 bg-gradient-to-br from-brand/8 via-transparent to-brand-gold/8" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand">
              <MapPin className="h-3.5 w-3.5" /> Para la comunidad latina en EE.UU.
            </span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-brand-ink sm:text-5xl lg:text-6xl">
              Encuentra negocios latinos de <span className="text-brand">confianza</span> cerca de ti
            </h1>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              Directorio en español con fichas verificadas, lugares para visitar y un newsletter con lo mejor de tu comunidad.
            </p>
            <form onSubmit={go} className="mt-7 flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="hero-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Busca restaurantes, barberías, mecánicos..."
                  className="h-14 w-full rounded-full border border-input bg-white pl-12 pr-4 text-base shadow-sm outline-none transition-shadow focus:ring-2 focus:ring-brand/40"
                />
              </div>
              <button
                data-testid="hero-search-button"
                type="submit"
                className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-brand px-8 text-base font-bold text-white transition-colors hover:bg-brand-hover"
              >
                Buscar <ArrowRight className="h-5 w-5" />
              </button>
            </form>
            {stats && (
              <div className="mt-6 flex flex-wrap gap-x-7 gap-y-2 text-sm">
                <Stat n={stats.businesses} l="negocios" />
                <Stat n={stats.verified} l="verificados" />
                <Stat n={stats.places} l="lugares" />
                <Stat n={stats.markets} l="mercados semilla" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Explora por categoría</h2>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {cats.map((c, i) => {
            const Icon = ICONS[c.icon] || Store;
            return (
              <motion.button
                key={c.slug}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                data-testid={`category-${c.slug}`}
                onClick={() => nav(`/explorar?category=${c.slug}`)}
                className="group flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </span>
                <span className="text-sm font-semibold text-brand-ink">{c.name}</span>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Negocios destacados</h2>
          <Link to="/explorar" data-testid="see-all-link" className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
            Ver todos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((b) => (
            <BusinessCard key={b.id} b={b} onContact={(id, type) => api.track({ business_id: id, type })} />
          ))}
        </div>
      </section>

      {/* Directory by city (SEO landing links) */}
      <section className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Directorio por ciudad</h2>
        <p className="mt-1 text-sm text-muted-foreground">Mercados semilla con negocios verificados de alta calidad.</p>
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {cities.map((m, i) => (
            <motion.div key={m.slug} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Link
                to={`/directorio/${m.slug}`}
                data-testid={`home-city-${m.slug}`}
                className="group flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-4 transition-all hover:-translate-y-1 hover:border-brand/40 hover:shadow-lg"
              >
                <span className="flex items-center gap-2 font-display font-bold text-brand-ink">
                  <MapPin className="h-4 w-4 text-brand" /> {m.city}
                </span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-muted-foreground">{m.count}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Claim CTA */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-brand-ink p-8 sm:p-12">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-brand/30 blur-3xl" />
          <div className="relative max-w-xl">
            <ShieldCheck className="h-9 w-9 text-brand-gold" />
            <h2 className="mt-3 font-display text-2xl font-bold text-white sm:text-3xl">¿Es tu negocio?</h2>
            <p className="mt-2 text-white/70">
              Reclama tu ficha gratis con verificación por email, edita tus datos y destácate ante miles de clientes latinos.
            </p>
            <Link
              to="/explorar"
              data-testid="claim-cta-button"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-brand-ink transition-transform hover:scale-105"
            >
              Encuentra y reclama tu ficha <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="font-display text-xl font-extrabold text-brand">{n ?? "—"}</span>
    <span className="text-muted-foreground">{l}</span>
  </div>
);
